// Normaliza una celda de fecha de Excel a ISO `YYYY-MM-DD`.
//
// Por qué existe: cuando el usuario escribe una fecha en Excel, la celda se
// guarda como un SERIAL numérico (no texto). Leerla con `raw:false` la formatea
// según el formato de la celda (ej. `m/d/yy` → "6/4/26"), que NO matchea el
// `YYYY-MM-DD` con el que comparamos contra los días de la semana → toda fila
// daba "fecha fuera de la semana".
//
// Solución: tomar el VALOR CRUDO de la celda (serial vía `raw:true`) y formatear
// el serial con `XLSX.SSF.parse_date_code` — aritmética de enteros, inmune a la
// zona horaria del navegador. Acepta además texto ISO y `d/m/y` · `m/d/y`
// (heurística: el componente > 12 es el día; si ambos ≤ 12, asume d/m, es-CO).

import * as XLSX from 'xlsx';

export function parseFechaISO(cell: unknown): string {
  if (cell == null || cell === '') return '';

  // Serial de Excel (lo más común para fechas reales escritas en la app).
  if (typeof cell === 'number' && Number.isFinite(cell)) {
    const o = XLSX.SSF.parse_date_code(cell);
    if (o && o.y) {
      return `${String(o.y).padStart(4, '0')}-${String(o.m).padStart(2, '0')}-${String(o.d).padStart(2, '0')}`;
    }
    return String(cell);
  }

  const s = String(cell).trim();

  // Texto ISO (YYYY-MM-DD, con o sin hora). Si el "mes" es >12 y el "día" ≤12,
  // el usuario escribió AÑO-DÍA-MES (Excel real de Sopó: "2026-29-06") → swap,
  // misma heurística que d/m vs m/d de abajo.
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    let mon = Number(m[2]);
    let day = Number(m[3]);
    if (mon > 12 && day <= 12) [mon, day] = [day, mon];
    return `${m[1]}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  // Texto con barras: d/m/y o m/d/y (heurística + default es-CO d/m).
  m = s.match(/^(\d{1,2})[/](\d{1,2})[/](\d{2,4})$/);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    let y = Number(m[3]);
    if (y < 100) y += 2000;
    let day: number;
    let mon: number;
    if (a > 12) {
      day = a;
      mon = b;
    } else if (b > 12) {
      day = b;
      mon = a;
    } else {
      // ambiguo → es-CO usa d/m/y
      day = a;
      mon = b;
    }
    return `${y}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return s; // no reconocido → se reporta como "fuera de la semana" en el preview
}

// ¿Es una fecha ISO de calendario REAL? El regex 4-2-2 deja pasar "2026-31-06"
// o "2026-02-30", que después revientan el Zod compartido (z.string().date())
// con un toast genérico. El round-trip por Date en UTC detecta el overflow
// (2026-06-31 → 2026-07-01 ≠ input).
export function esFechaISOValida(f: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f)) return false;
  const d = new Date(`${f}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === f;
}
